/**
 * Pure state-derivation for the agentic-room composer.
 *
 * Splitting this out keeps the React component declarative ("render given a
 * state object") and makes every UI variant unit-testable without mounting
 * the DOM. The component just imports `deriveComposerState` and `canSendNow`.
 *
 * State machine (from `<Composer>` perspective):
 *
 *   ┌──────────┐  EXECUTION_STARTED  ┌──────────┐  APPROVAL_REQUESTED  ┌────────────────────┐
 *   │   IDLE   │ ──────────────────▶ │ RUNNING  │ ──────────────────▶ │ AWAITING_APPROVAL  │
 *   │          │                     │          │                     │ (claim → resolve)   │
 *   └──────────┘ ◀──────── EXECUTION_COMPLETED / FAILED ──────────────└────────────────────┘
 *
 * `modifyMode` is a sub-state of AWAITING_APPROVAL — entered when the
 * caller has clicked "Modify". The composer prompt becomes editable and
 * Send routes to `pool_approval_handler` with `decision: MODIFY`. Approve
 * / Deny stay direct (no text required).
 */

export const COMPOSER_STATES = Object.freeze({
  IDLE: 'IDLE',
  RUNNING: 'RUNNING',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
});

/**
 * Derive the composer state from the room reducer outputs.
 *
 * @param {{ uiState?: string|null, pendingApproval?: { taskToken?: string }|null }} input
 * @returns {'IDLE'|'RUNNING'|'AWAITING_APPROVAL'}
 */
export function deriveComposerState({ uiState, pendingApproval } = {}) {
  if (pendingApproval && pendingApproval.taskToken) return COMPOSER_STATES.AWAITING_APPROVAL;
  const u = (uiState || '').toUpperCase();
  if (u === COMPOSER_STATES.RUNNING) return COMPOSER_STATES.RUNNING;
  return COMPOSER_STATES.IDLE;
}

/**
 * Should the prompt textarea accept keystrokes right now?
 *
 *   - frozen: never (PAUSED / RETIRED / ARCHIVED — backend rejects too).
 *   - IDLE: editable when caller has execute permission.
 *   - RUNNING: never (everyone, including owner, locked out by design).
 *   - AWAITING_APPROVAL: only when this user clicked "Modify" *and* holds
 *     the approval claim. Pool peers see the input disabled; the claim
 *     holder enters modify mode by clicking the Modify button.
 *
 * @param {{
 *   composerState: string,
 *   canExecute: boolean,
 *   modifyMode: boolean,
 *   iHoldClaim: boolean,
 *   busy: boolean,
 *   frozen?: boolean,
 * }} args
 * @returns {boolean}
 */
export function isPromptEditable({ composerState, canExecute, modifyMode, iHoldClaim, busy, frozen }) {
  if (frozen) return false;
  if (busy) return false;
  if (composerState === COMPOSER_STATES.IDLE) return Boolean(canExecute);
  if (composerState === COMPOSER_STATES.RUNNING) return false;
  if (composerState === COMPOSER_STATES.AWAITING_APPROVAL) return Boolean(modifyMode && iHoldClaim);
  return false;
}

/**
 * Is the Send button currently primed to fire? Composer renders the same
 * button across IDLE and modifyMode; the action it triggers depends on
 * `composerState` and is wired by the caller.
 *
 * The ``frozen`` flag short-circuits everything: PAUSED / RETIRED /
 * ARCHIVED pools never accept new prompts or approvals, regardless of
 * role / claim state.
 */
export function canSendNow({
  composerState,
  canExecute,
  modifyMode,
  iHoldClaim,
  promptText,
  hasUploading,
  busy,
  frozen,
  mcpGateReason,
}) {
  if (frozen) return false;
  if (busy || hasUploading) return false;
  // Pre-flight MCP gate: an enabled authenticated MCP has no credential for
  // this room. Block Send until configured (avoids a mid-run stall the
  // builder can't recover from — MCP servers are wired before it starts).
  if (mcpGateReason) return false;
  const trimmed = (promptText || '').trim();
  if (composerState === COMPOSER_STATES.IDLE) return Boolean(canExecute) && trimmed.length > 0;
  if (composerState === COMPOSER_STATES.AWAITING_APPROVAL) {
    return Boolean(modifyMode && iHoldClaim) && trimmed.length > 0;
  }
  return false;
}

/**
 * Placeholder copy for the textarea. Surfaced as a centralized list so the
 * room never disagrees with the composer about state semantics.
 *
 * ``frozenReason`` (when supplied) wins over every other variant — the
 * lifecycle freeze is the strongest gate the composer can render.
 */
export function placeholderFor({ composerState, canExecute, modifyMode, iHoldClaim, peerLabel, frozen, frozenReason, mcpGateReason }) {
  if (frozen) return frozenReason || 'This network is frozen — sending is disabled.';
  if (mcpGateReason) return mcpGateReason;
  if (composerState === COMPOSER_STATES.AWAITING_APPROVAL) {
    if (modifyMode && iHoldClaim) return 'Describe the modification or correction…';
    if (iHoldClaim) return "Click 'Modify' to draft changes, or Approve / Deny.";
    if (peerLabel) return `Waiting on ${peerLabel}…`;
    return 'Waiting for an approver…';
  }
  if (composerState === COMPOSER_STATES.RUNNING) return 'Network is running…';
  if (!canExecute) return 'Read-only on this pool.';
  return 'Describe your agentic network requirements…';
}
